import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@stackje/shared-components/ui/button";
import {
  ClearableInput,
  ClearableTextarea,
} from "@stackje/shared-components/ui/clearable-fields";
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@stackje/shared-components/ui/dialog";
import { Label } from "@stackje/shared-components/ui/label";
import type { SelectedImageData } from "@/components/shared/ImageEditor";
import { FormImageUploader } from "@/components/shared/FormImageUploader";
import { UnsavedChangesConfirmDialog } from "@/components/shared/UnsavedChangesConfirmDialog";

// Zod schema for feature validation
const featureSchema = z.object({
  title: z.string().min(1, "Feature title is required"),
  description: z.string().min(1, "Feature description is required"),
  image: z
    .object({
      src: z.string(),
      alt: z.string().optional(),
    })
    .optional(),
});

type FeatureFormData = z.infer<typeof featureSchema>;

interface FeatureEditFormDialogContentProps {
  isEditing: boolean;
  initialData?: FeatureFormData;
  onSubmit: (data: FeatureFormData) => void;
  onClose?: () => void;
  editingFeatureIndex?: number | null; // Optional, for unique name/id generation
}

export function FeatureEditFormDialogContent({
  isEditing,
  initialData,
  onSubmit,
  onClose,
  editingFeatureIndex,
}: FeatureEditFormDialogContentProps) {
  const form = useForm<FeatureFormData>({
    resolver: zodResolver(featureSchema),
    defaultValues: {
      title: initialData?.title || "",
      description: initialData?.description || "",
    },
    mode: "onChange",
  });

  // Local state for image since it's not a simple form field
  const [imageValue, setImageValue] = useState<SelectedImageData | null>(
    initialData?.image
      ? { fullUrl: initialData.image.src, previewUrl: initialData.image.src }
      : null
  );

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const isDirty =
    form.formState.isDirty ||
    (imageValue?.fullUrl !== initialData?.image?.src &&
      imageValue?.fullUrl !== null);

  const handleSubmit = form.handleSubmit((data) => {
    // Transform image data if needed
    const submitData: FeatureFormData = {
      ...data,
      image: imageValue?.fullUrl
        ? {
            src: imageValue.fullUrl,
            alt: data.title || "Feature image",
          }
        : undefined,
    };
    onSubmit(submitData);
    form.reset();
  });

  // Reset form when editing different feature (initialData changes) or when switching between add/edit modes
  useEffect(() => {
    if (initialData) {
      form.reset({
        title: initialData.title || "",
        description: initialData.description || "",
      });
      // Also update the image preview
      setImageValue(
        initialData.image
          ? {
              fullUrl: initialData.image.src,
              previewUrl: initialData.image.src,
            }
          : null
      );
    } else {
      // When adding new feature, reset form
      form.reset({
        title: "",
        description: "",
      });
      setImageValue(null);
    }
  }, [initialData, isEditing, form]);

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmDialog(true);
    } else {
      onClose?.();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onClose?.();
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <DialogContent
        className="w-full h-[100dvh] sm:max-w-[425px] sm:w-[90vw] sm:h-auto sm:max-h-[85vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        onClose={handleClose}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {isEditing
              ? `Edit Feature ${(editingFeatureIndex ?? 0) + 1}`
              : "Add Feature"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 p-4 flex-1 overflow-y-auto min-w-0">
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex flex-col mb-2">
              <p className="text-sm text-muted-foreground">
                Not all fields will be used for a given card type.
              </p>
              <p className="text-sm text-muted-foreground">
                <span className="text-red-500">*</span> indicates required fields
              </p>
            </div>
            <Label htmlFor="featureTitle">Title <span className="text-red-500">*</span></Label>
            <Controller
              name="title"
              control={form.control}
              render={({ field, fieldState }) => (
                <>
                  <ClearableInput
                    id="featureTitle"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter feature title"
                  />
                  {fieldState.error && (
                    <p className="mt-1 text-sm text-red-600">
                      {fieldState.error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-0">
            <Label htmlFor="featureDescription">Description <span className="text-red-500">*</span></Label>
            <Controller
              name="description"
              control={form.control}
              render={({ field, fieldState }) => (
                <>
                  <ClearableTextarea
                    id="featureDescription"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter feature description"
                    rows={3}
                  />
                  {fieldState.error && (
                    <p className="mt-1 text-sm text-red-600">
                      {fieldState.error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div className="flex flex-col gap-2 min-w-0">
            <FormImageUploader
              label="Feature Image"
              currentImage={imageValue}
              onImageChange={setImageValue}
              targetCropWidth={150}
              targetCropHeight={150}
              allowFreeCrop={true}
              imagePreviewClassName="max-w-[150px] h-auto rounded border object-cover aspect-square"
            />
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 sticky bottom-0 bg-background border-t pt-4 mt-auto">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={form.formState.isSubmitting || !form.formState.isValid}
            className="w-full"
          >
            {isEditing ? "Save Feature" : "Add Feature"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <UnsavedChangesConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}
